import React from 'react';
import { Settings, Mail, Ellipsis } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { canAccessAiEmailReview, hasAdminEquivalentAccess } from '@/utils/swaRole';
import { Button, Tabs } from '@/ui/components';
import {
  COMPANY_AI_WORKSPACE_TABS,
  getAccessibleCompanyAiWorkspaceTabs,
  type CompanyAiWorkspaceTabId,
} from './companyAiWorkspaceTabs';

export type { CompanyAiWorkspaceTabId } from './companyAiWorkspaceTabs';
type CompanyAiPageId = 'workspace' | 'admin' | 'email';

export function getCompanyAiWorkspaceTabFromSearch(search: string): CompanyAiWorkspaceTabId {
  const params = new URLSearchParams(search);
  const tab = params.get('tab');
  return COMPANY_AI_WORKSPACE_TABS.some((item) => item.id === tab)
    ? (tab as CompanyAiWorkspaceTabId)
    : 'ask-ai';
}

type CompanyAiNavigationTabsProps = {
  currentPage: CompanyAiPageId;
  activeWorkspaceTab: CompanyAiWorkspaceTabId;
  userEmail?: string | null;
  userRoles?: string[] | null;
  onWorkspaceTabChange?: (tabId: CompanyAiWorkspaceTabId) => void;
};

export default function CompanyAiNavigationTabs({
  currentPage,
  activeWorkspaceTab,
  userEmail,
  userRoles,
  onWorkspaceTabChange,
}: CompanyAiNavigationTabsProps) {
  const navigate = useNavigate();
  const canManageKnowledgeSources = hasAdminEquivalentAccess(userRoles, userEmail);
  const canAccessEmailReview = canAccessAiEmailReview(userEmail);
  const workspaceTabs = getAccessibleCompanyAiWorkspaceTabs(userRoles, userEmail);

  function handleWorkspaceTabChange(tabId: string) {
    if (currentPage === 'workspace') {
      onWorkspaceTabChange?.(tabId as CompanyAiWorkspaceTabId);
      return;
    }

    navigate(`/ai?tab=${tabId}`);
  }

  const toolActions = (
        <div className="company-ai-tabs__actions">
          {canManageKnowledgeSources ? (
            <Button
              type="button"
              variant={currentPage === 'admin' ? 'primary' : 'secondary'}
              size="sm"
              className="company-ai-tabs__admin-entry pm-icon-button"
              aria-label="AI Admin" title="AI Admin" onClick={() => navigate('/ai/admin')}
            >
              <Settings size={18} aria-hidden="true" />
            </Button>
          ) : null}
          {canAccessEmailReview ? (
            <Button
              type="button"
              variant={currentPage === 'email' ? 'primary' : 'secondary'}
              size="sm"
              className="company-ai-tabs__admin-entry"
              aria-label="Email Suggestions" title="Email Suggestions" onClick={() => navigate('/ai/email')}
            >
              <Mail size={18} aria-hidden="true" />
            </Button>
          ) : null}
        </div>
  );

  return (
    <Tabs
      className="company-ai-tabs company-ai-tabs--navigation-only"
      activeId={activeWorkspaceTab}
      onTabChange={handleWorkspaceTabChange}
      actions={canManageKnowledgeSources || canAccessEmailReview ? (
        activeWorkspaceTab === 'pm-agent' && currentPage === 'workspace' ?
          <details className="company-ai-tool-menu"><summary aria-label="AI tools options" title="AI tools options"><Ellipsis size={20} aria-hidden="true" /></summary>{toolActions}</details>
          : toolActions
      ) : null}
      tabs={workspaceTabs.map((tab) => ({
        id: tab.id,
        label: tab.label,
        content: null,
      }))}
    />
  );
}
